/**
 * Dither-thinned surfaces (05 §8.1). Nothing in the scene pass is alpha-blended: smoke, a
 * searchlight beam and anything else that must look translucent is an ordinary opaque toon
 * material that DISCARDS pixels on a 4x4 Bayer screen. The screen is anchored to the world
 * texel grid (the same `uCamTexel` the terrain and the palette lock use), so the holes stay
 * put on the ground while the camera scrolls instead of swimming across it.
 */

import { DoubleSide, FrontSide, MeshToonMaterial, type Texture, Vector3 } from 'three';
import { OUTLINE, type OutlineClass, patchMrt, sharedUniforms } from './toon';

/** Number of distinct coverages a 4x4 screen can show; anything finer is rounded to one of these. */
export const DITHER_LEVELS = 16;

/** Snap a 0..1 coverage to the screen's sixteen levels, so a fade moves in visible steps. */
export const ditherStep = (coverage: number): number =>
  Math.round(Math.max(0, Math.min(1, coverage)) * DITHER_LEVELS) / DITHER_LEVELS;

export interface DitherOptions {
  gradientMap: Texture;
  /** Share of pixels kept, 0..1. The owner keeps this object and changes `.value` as the thing ages. */
  coverage: { value: number };
  outline?: OutlineClass;
  /** Self-lit (a beam of light): the vertex colour is added as emission, as `createEmissiveMaterial` does. */
  emissiveBoost?: number;
  /**
   * A thinner patch round one point of the SCREEN - the player inside his own smoke. x, y are
   * scene-target pixels (origin bottom-left), z the radius in pixels (0 = no patch). Shared object.
   */
  hole?: { value: Vector3 };
  /** Coverage inside the hole; the 35 % of 05 §8.1 by default. */
  holeCoverage?: number;
  doubleSided?: boolean;
}

const DITHER_DECL = /* glsl */ `
uniform float uCoverage;
uniform vec2 uCamTexel;
uniform vec3 uHole;
uniform float uHoleCoverage;
const float DITHER_BAYER[16] = float[16](
   0.0,  8.0,  2.0, 10.0,
  12.0,  4.0, 14.0,  6.0,
   3.0, 11.0,  1.0,  9.0,
  15.0,  7.0, 13.0,  5.0);
`;

const DITHER_DISCARD = /* glsl */ `
  {
    float keep = uCoverage;
    if (uHole.z > 0.0 && distance(gl_FragCoord.xy, uHole.xy) < uHole.z) keep = min(keep, uHoleCoverage);
    ivec2 ditherCell = ivec2(floor(gl_FragCoord.xy)) + ivec2(uCamTexel);
    float threshold = (DITHER_BAYER[(ditherCell.x & 3) + (ditherCell.y & 3) * 4] + 0.5) / 16.0;
    if (threshold > keep) discard;
  }
`;

const NO_HOLE = { value: new Vector3(0, 0, 0) };

/** Vertex-coloured toon material that keeps only `coverage` of its pixels. Dispose it with its owner. */
export function createDitherMaterial(opts: DitherOptions): MeshToonMaterial {
  const material = new MeshToonMaterial({
    vertexColors: true,
    gradientMap: opts.gradientMap,
    side: opts.doubleSided ? DoubleSide : FrontSide,
  });
  const boost = opts.emissiveBoost ?? 0;
  material.onBeforeCompile = (shader) => {
    patchMrt(shader, opts.outline ?? OUTLINE.none);
    const u = shader.uniforms;
    u['uCoverage'] = opts.coverage;
    u['uCamTexel'] = sharedUniforms.uCamTexel;
    u['uHole'] = opts.hole ?? NO_HOLE;
    u['uHoleCoverage'] = { value: opts.holeCoverage ?? 0.35 };
    u['uBoost'] = { value: boost };
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', `${DITHER_DECL}uniform float uBoost;\nvoid main() {\n${DITHER_DISCARD}`)
      .replace(
        '#include <emissivemap_fragment>',
        '#include <emissivemap_fragment>\n  totalEmissiveRadiance += vColor.rgb * uBoost;',
      );
  };
  material.customProgramCacheKey = () => 'toon-mrt-dither';
  return material;
}
